import { ApiResponse } from '@shared/types';
import { REPORTS_IPC_CHANNELS } from '../../../utils/constants';
import { isElectron } from '../util';
import { downloadPdfFromBase64 } from '../util/pdfDownload';
import {
  AccommodationDailySummary,
  AccommodationOccupiedBooking,
  AccommodationPaymentDetail,
  AccommodationPaymentMethodSummary,
  DepartmentSalesSummary,
  ExportSalesReportPdfRequest,
  ExportSalesReportPdfResponse,
  ItemCancellationView,
  ItemSalesSummaryView,
  OrderSettlementView,
  OutletSalesReport,
  PaymentMethodSalesSummary,
  SalesReportCreditorView,
  SalesReportDiscountView,
  SalesReportOrderView,
  SalesReportSummary,
  SalesReportUserSummary,
} from '../types/report';
import { supabase } from './supabase';

const SALES_REPORT_CACHE_TTL_MS = 5 * 60 * 1000;

const salesReportCache = new Map<
  string,
  { data: OutletSalesReport; fetchedAt: number }
>();
const salesReportInFlight = new Map<
  string,
  Promise<ApiResponse<OutletSalesReport>>
>();

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const mapUserSummary = (value: any): SalesReportUserSummary => ({
  id: value?.id ?? null,
  first_name: value?.first_name ?? null,
  last_name: value?.last_name ?? null,
  outlet_id: value?.outlet_id ?? null,
});

const mapSummary = (value: any): SalesReportSummary => ({
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  settled_order_count: toNumber(value.settled_order_count),
  settlement_count: toNumber(value.settlement_count),
  gross_sale: toNumber(value.gross_sale),
  total_discount: toNumber(value.total_discount),
  total_sale: toNumber(value.total_sale),
  total_settlement_amount: toNumber(value.total_settlement_amount),
  average_bill_amount: toNumber(value.average_bill_amount),
});

const mapPaymentMethodSummary = (value: any): PaymentMethodSalesSummary => ({
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  payment_method_id: toNumber(value.payment_method_id),
  payment_method_name: value.payment_method_name || '',
  settlement_count: toNumber(value.settlement_count),
  settled_order_count: toNumber(value.settled_order_count),
  total_amount: toNumber(value.total_amount),
});

const mapDepartmentSummary = (value: any): DepartmentSalesSummary => ({
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  department_id: value.department_id ?? null,
  department_name: value.department_name || 'Unassigned',
  item_count: toNumber(value.item_count),
  quantity_sold: toNumber(value.quantity_sold),
  gross_sale: toNumber(value.gross_sale),
  total_discount: toNumber(value.total_discount),
  total_sale: toNumber(value.total_sale),
});

const mapSettlement = (value: any): OrderSettlementView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  amount: toNumber(value.amount),
  payment_method_id: toNumber(value.payment_method_id),
  payment_method_name: value.payment_method_name || '',
  notes: value.notes ?? null,
  order_total_amount: toNumber(value.order_total_amount),
  order_discount_amount: toNumber(value.order_discount_amount),
  order_net_amount: toNumber(value.order_net_amount),
  order_balance_amount: toNumber(value.order_balance_amount),
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapDiscount = (value: any): SalesReportDiscountView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  amount: toNumber(value.amount),
  notes: value.notes ?? null,
  added_by: mapUserSummary(value.added_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapCreditor = (value: any): SalesReportCreditorView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  client_email: value.client_email ?? null,
  client_contact: value.client_contact ?? null,
  amount: toNumber(value.amount),
  payment_method_id: toNumber(value.payment_method_id),
  payment_method_name: value.payment_method_name || '',
  notes: value.notes ?? null,
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapOrder = (value: any): SalesReportOrderView => ({
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  status_id: toNumber(value.status_id),
  status_name: value.status_name || '',
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  item_count: toNumber(value.item_count),
  pending_item_count: toNumber(value.pending_item_count),
  discount_amount: toNumber(value.discount_amount),
  total_amount: toNumber(value.total_amount),
  net_amount: toNumber(value.net_amount),
  settlement_total: toNumber(value.settlement_total),
  settlement_count: toNumber(value.settlement_count),
  balance_amount: toNumber(value.balance_amount),
  is_fully_settled: Boolean(value.is_fully_settled),
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapItemCancellation = (value: any): ItemCancellationView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  item_name: value.item_name || '',
  unit_price: toNumber(value.unit_price),
  reason: value.reason || '',
  cancelled_by: mapUserSummary(value.cancelled_by),
  created_at: value.created_at,
});

const mapItemSummary = (value: any): ItemSalesSummaryView => ({
  id: value.id,
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  menu_item_id: value.menu_item_id,
  item_name: value.item_name || '',
  department_id: value.department_id ?? null,
  department_name: value.department_name || 'Unassigned',
  category_id: value.category_id ?? null,
  category_name: value.category_name || '',
  unit_price: toNumber(value.unit_price),
  order_count: toNumber(value.order_count),
  quantity_sold: toNumber(value.quantity_sold),
  gross_amount: toNumber(value.gross_amount),
});

const aggregateItemSummaries = (
  rows: ItemSalesSummaryView[],
): ItemSalesSummaryView[] =>
  Array.from(
    rows
      .reduce<Map<string, ItemSalesSummaryView>>((acc, row) => {
        const key = row.menu_item_id;
        const existing = acc.get(key);
        if (existing) {
          existing.order_count += row.order_count;
          existing.quantity_sold += row.quantity_sold;
          existing.gross_amount += row.gross_amount;
          return acc;
        }
        acc.set(key, { ...row });
        return acc;
      }, new Map())
      .values(),
  ).sort(
    (a, b) =>
      a.department_name.localeCompare(b.department_name) ||
      a.item_name.localeCompare(b.item_name),
  );

const buildEmptySummary = (
  outletId: string,
  companyId: number,
  outletName: string,
  businessDate: string,
): SalesReportSummary => ({
  sale_date: businessDate,
  outlet_id: outletId,
  company_id: companyId,
  outlet_name: outletName,
  settled_order_count: 0,
  settlement_count: 0,
  gross_sale: 0,
  total_discount: 0,
  total_sale: 0,
  total_settlement_amount: 0,
  average_bill_amount: 0,
});

const buildSalesReportCacheKey = ({
  companyId,
  outletId,
  businessDate,
}: {
  companyId: number;
  outletId: string | null;
  businessDate: string;
}): string =>
  ['sales-report', companyId, outletId || 'all-outlets', businessDate].join(
    '::',
  );

const resolveCachedSalesReport = (
  cacheKey: string,
): OutletSalesReport | null => {
  const cached = salesReportCache.get(cacheKey);
  if (!cached) return null;

  if (Date.now() - cached.fetchedAt > SALES_REPORT_CACHE_TTL_MS) {
    salesReportCache.delete(cacheKey);
    return null;
  }

  return cached.data;
};

const applyScope = <T extends { eq: Function }>(
  query: T,
  companyId: number,
  outletId: string | null,
) => {
  if (outletId) {
    return query.eq('outlet_id', outletId);
  }

  return query.eq('company_id', companyId);
};

const aggregatePaymentMethodSummaries = (
  rows: PaymentMethodSalesSummary[],
): PaymentMethodSalesSummary[] =>
  Array.from(
    rows
      .reduce<Map<string, PaymentMethodSalesSummary>>((accumulator, row) => {
        const key = `${row.payment_method_id}::${row.payment_method_name}`;
        const existing = accumulator.get(key);

        if (existing) {
          existing.settlement_count += row.settlement_count;
          existing.settled_order_count += row.settled_order_count;
          existing.total_amount += row.total_amount;
          return accumulator;
        }

        accumulator.set(key, { ...row });
        return accumulator;
      }, new Map<string, PaymentMethodSalesSummary>())
      .values(),
  ).sort((left, right) =>
    left.payment_method_name.localeCompare(right.payment_method_name),
  );

const aggregateDepartmentSummaries = (
  rows: DepartmentSalesSummary[],
): DepartmentSalesSummary[] =>
  Array.from(
    rows
      .reduce<Map<string, DepartmentSalesSummary>>((accumulator, row) => {
        const key = `${row.department_id || row.department_name}`;
        const existing = accumulator.get(key);

        if (existing) {
          existing.item_count += row.item_count;
          existing.quantity_sold += row.quantity_sold;
          existing.gross_sale += row.gross_sale;
          existing.total_discount += row.total_discount;
          existing.total_sale += row.total_sale;
          return accumulator;
        }

        accumulator.set(key, { ...row });
        return accumulator;
      }, new Map<string, DepartmentSalesSummary>())
      .values(),
  ).sort((left, right) =>
    left.department_name.localeCompare(right.department_name),
  );

const buildSummary = ({
  rows,
  outletId,
  companyId,
  outletName,
  businessDate,
}: {
  rows: SalesReportSummary[];
  outletId: string | null;
  companyId: number;
  outletName: string;
  businessDate: string;
}): SalesReportSummary => {
  if (rows.length === 0) {
    return buildEmptySummary(
      outletId || 'all-outlets',
      companyId,
      outletName,
      businessDate,
    );
  }

  if (rows.length === 1) {
    return {
      ...rows[0],
      outlet_id: outletId || rows[0].outlet_id,
      outlet_name: outletName,
    };
  }

  const grossSale = rows.reduce((sum, row) => sum + row.gross_sale, 0);
  const totalDiscount = rows.reduce((sum, row) => sum + row.total_discount, 0);
  const totalSale = rows.reduce((sum, row) => sum + row.total_sale, 0);
  const totalSettlementAmount = rows.reduce(
    (sum, row) => sum + row.total_settlement_amount,
    0,
  );
  const settledOrderCount = rows.reduce(
    (sum, row) => sum + row.settled_order_count,
    0,
  );
  const settlementCount = rows.reduce(
    (sum, row) => sum + row.settlement_count,
    0,
  );

  return {
    sale_date: businessDate,
    outlet_id: outletId || 'all-outlets',
    company_id: companyId,
    outlet_name: outletName,
    settled_order_count: settledOrderCount,
    settlement_count: settlementCount,
    gross_sale: grossSale,
    total_discount: totalDiscount,
    total_sale: totalSale,
    total_settlement_amount: totalSettlementAmount,
    average_bill_amount:
      settledOrderCount > 0 ? totalSale / settledOrderCount : 0,
  };
};

const fetchAccommodationDailySummary = async (
  companyId: number,
  outletId: string | null,
  businessDate: string,
): Promise<AccommodationDailySummary | null> => {
  try {
    let propertyIds: string[];

    if (outletId) {
      const { data: properties } = await supabase
        .from('accommodation_properties')
        .select('id')
        .eq('outlet_id', outletId);
      propertyIds = (properties || []).map((p: any) => p.id as string);
    } else {
      const { data: outlets } = await supabase
        .from('outlets')
        .select('id')
        .eq('company_id', companyId);
      const outletIds = (outlets || []).map((o: any) => o.id as string);
      if (outletIds.length === 0) return null;
      const { data: properties } = await supabase
        .from('accommodation_properties')
        .select('id')
        .in('outlet_id', outletIds);
      propertyIds = (properties || []).map((p: any) => p.id as string);
    }

    if (propertyIds.length === 0) return null;

    const { data: bookingRows } = await supabase
      .from('accommodation_bookings')
      .select('id')
      .in('property_id', propertyIds);
    const bookingIds = (bookingRows ?? []).map((b: any) => b.id as string);

    const [
      dailyRevenueResult,
      arrivalsResult,
      departuresResult,
      detailedPaymentsResult,
      occupiedBookingsResult,
    ] = await Promise.all([
      supabase
        .from('accommodation_booking_daily_revenue')
        .select('daily_revenue')
        .in('property_id', propertyIds)
        .eq('stay_date', businessDate),
      supabase
        .from('accommodation_bookings')
        .select('id')
        .in('property_id', propertyIds)
        .eq('check_in', businessDate)
        .in('status', ['confirmed', 'checked_in']),
      supabase
        .from('accommodation_bookings')
        .select('id')
        .in('property_id', propertyIds)
        .eq('check_out', businessDate),
      bookingIds.length === 0
        ? Promise.resolve({ data: [] as any[] })
        : supabase
            .from('accommodation_booking_payments')
            .select(
              'id, amount, paid_at, payment_methods(name), accommodation_bookings!booking_id(check_in, check_out, clients(first_name, last_name), accommodation_booking_rooms(price_per_night, accommodation_rooms(room_number)))',
            )
            .in('booking_id', bookingIds)
            .gte('paid_at', `${businessDate}T00:00:00`)
            .lte('paid_at', `${businessDate}T23:59:59`)
            .order('paid_at', { ascending: true }),
      supabase
        .from('accommodation_bookings')
        .select(
          'id, check_in, check_out, adults, children, total_amount, clients(first_name, last_name), accommodation_booking_rooms(price_per_night, accommodation_rooms(room_number))',
        )
        .in('property_id', propertyIds)
        .eq('status', 'checked_in')
        .gte('check_out', businessDate)
        .order('check_in', { ascending: true }),
    ]);

    const roomRevenue = (dailyRevenueResult.data || []).reduce(
      (sum: number, r: any) => sum + (r.daily_revenue as number),
      0,
    );

    const rawPayments: any[] = detailedPaymentsResult.data ?? [];
    const paymentsTotal = rawPayments.reduce(
      (sum: number, p: any) => sum + toNumber(p.amount),
      0,
    );
    const byMethodMap = rawPayments.reduce<
      Map<string, { total_amount: number; payment_count: number }>
    >((acc, p) => {
      const name: string = p.payment_methods?.name ?? 'Unknown';
      const existing = acc.get(name);
      if (existing) {
        existing.total_amount += toNumber(p.amount);
        existing.payment_count += 1;
      } else {
        acc.set(name, { total_amount: toNumber(p.amount), payment_count: 1 });
      }
      return acc;
    }, new Map());
    const paymentsByMethod: AccommodationPaymentMethodSummary[] = Array.from(
      byMethodMap.entries(),
    ).map(([methodName, v]) => ({
      payment_method_name: methodName,
      ...v,
    }));

    const payments: AccommodationPaymentDetail[] = rawPayments.map((p: any) => {
      const booking = Array.isArray(p.accommodation_bookings)
        ? p.accommodation_bookings[0]
        : p.accommodation_bookings;
      const bookingRoom = Array.isArray(booking?.accommodation_booking_rooms)
        ? booking.accommodation_booking_rooms[0]
        : booking?.accommodation_booking_rooms;
      const client = booking?.clients;
      return {
        id: p.id,
        amount: toNumber(p.amount),
        paid_at: p.paid_at ?? '',
        payment_method_name: p.payment_methods?.name ?? 'Unknown',
        client_name:
          [client?.first_name, client?.last_name].filter(Boolean).join(' ') ||
          'Unknown',
        check_in: booking?.check_in ?? '',
        check_out: booking?.check_out ?? '',
        room_display:
          bookingRoom?.accommodation_rooms?.room_number ?? 'Unknown Room',
      };
    });

    const occupiedBookings: AccommodationOccupiedBooking[] = (
      occupiedBookingsResult.data ?? []
    ).map((b: any) => {
      const bookingRoom = Array.isArray(b.accommodation_booking_rooms)
        ? b.accommodation_booking_rooms[0]
        : b.accommodation_booking_rooms;
      return {
        id: b.id,
        client_name:
          [b.clients?.first_name, b.clients?.last_name]
            .filter(Boolean)
            .join(' ') || 'Unknown',
        check_in: b.check_in ?? '',
        check_out: b.check_out ?? '',
        adults: toNumber(b.adults),
        children: toNumber(b.children),
        room_display:
          bookingRoom?.accommodation_rooms?.room_number ?? 'Unknown Room',
        total_amount: toNumber(b.total_amount),
        price_per_night: toNumber(bookingRoom?.price_per_night ?? 0),
      };
    });

    return {
      roomRevenue,
      arrivalsCount: (arrivalsResult.data || []).length,
      departuresCount: (departuresResult.data || []).length,
      occupiedCount: occupiedBookings.length,
      paymentsTotal,
      paymentsByMethod,
      payments,
      occupiedBookings,
    };
  } catch {
    return null;
  }
};

export const fetchScopedSalesReport = async ({
  outletId,
  companyId,
  outletName,
  businessDate,
}: {
  outletId: string | null;
  companyId: number;
  outletName: string;
  businessDate: string;
}): Promise<ApiResponse<OutletSalesReport>> => {
  const cacheKey = buildSalesReportCacheKey({
    companyId,
    outletId,
    businessDate,
  });

  const cached = resolveCachedSalesReport(cacheKey);
  if (cached) {
    return {
      success: true,
      data: cached,
    };
  }

  const existingRequest = salesReportInFlight.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async (): Promise<ApiResponse<OutletSalesReport>> => {
    try {
      // Per-outlet: delegate to edge function to reduce client-side query load
      if (outletId) {
        const { data: edgeData, error: edgeError } =
          await supabase.functions.invoke('get-sales-report', {
            body: { outletId, companyId, outletName, businessDate },
          });
        if (edgeError) {
          return { success: false, error: edgeError.message, data: undefined };
        }
        const report = edgeData?.report as OutletSalesReport;
        if (!report) {
          return {
            success: false,
            error: 'No report returned from server',
            data: undefined,
          };
        }
        salesReportCache.set(cacheKey, { data: report, fetchedAt: Date.now() });
        return { success: true, data: report };
      }

      // Company-wide: run queries client-side with company_id scope
      const [
        summaryResponse,
        paymentResponse,
        departmentResponse,
        itemsResponse,
        settlements,
        discountsResponse,
        creditorsResponse,
        ordersResponse,
        itemCancellationsResponse,
        accommodationSummary,
      ] = await Promise.all([
        applyScope(
          supabase
            .from('sales_summary_view')
            .select('*')
            .eq('sale_date', businessDate),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('payment_method_sales_summary_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('payment_method_name', { ascending: true }),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('department_sales_summary_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('department_name', { ascending: true }),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('item_sales_summary_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('department_name', { ascending: true })
            .order('item_name', { ascending: true }),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('order_settlements_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('created_at', { ascending: false }),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('sales_discounts_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('created_at', { ascending: false }),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('sales_creditor_settlements_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('created_at', { ascending: false }),
          companyId,
          outletId,
        ),
        applyScope(
          supabase
            .from('sales_orders_view')
            .select('*')
            .eq('sale_date', businessDate)
            .order('created_at', { ascending: false })
            .limit(20),
          companyId,
          outletId,
        ),
        supabase
          .from('item_cancellations_report_view')
          .select('*')
          .eq('sale_date', businessDate)
          .eq('company_id', companyId)
          .order('created_at', { ascending: false }),
        fetchAccommodationDailySummary(companyId, outletId, businessDate),
      ]);

      if (summaryResponse.error) {
        return {
          success: false,
          error: summaryResponse.error.message,
          data: undefined,
        };
      }

      if (paymentResponse.error) {
        return {
          success: false,
          error: paymentResponse.error.message,
          data: undefined,
        };
      }

      if (departmentResponse.error) {
        return {
          success: false,
          error: departmentResponse.error.message,
          data: undefined,
        };
      }

      if (itemsResponse.error) {
        return {
          success: false,
          error: itemsResponse.error.message,
          data: undefined,
        };
      }

      if (settlements.error) {
        return {
          success: false,
          error: settlements.error.message,
          data: undefined,
        };
      }

      if (discountsResponse.error) {
        return {
          success: false,
          error: discountsResponse.error.message,
          data: undefined,
        };
      }

      if (creditorsResponse.error) {
        return {
          success: false,
          error: creditorsResponse.error.message,
          data: undefined,
        };
      }

      if (ordersResponse.error) {
        return {
          success: false,
          error: ordersResponse.error.message,
          data: undefined,
        };
      }

      const summaryRows = (summaryResponse.data || []).map(mapSummary);
      const paymentRows = (paymentResponse.data || []).map(
        mapPaymentMethodSummary,
      );
      const departmentRows = (departmentResponse.data || []).map(
        mapDepartmentSummary,
      );
      const itemRows = (itemsResponse.data || []).map(mapItemSummary);

      const report: OutletSalesReport = {
        summary: buildSummary({
          rows: summaryRows,
          outletId,
          companyId,
          outletName,
          businessDate,
        }),
        paymentMethods: aggregatePaymentMethodSummaries(paymentRows),
        departments: aggregateDepartmentSummaries(departmentRows),
        items: aggregateItemSummaries(itemRows),
        settlements: (settlements.data || []).map(mapSettlement),
        discounts: (discountsResponse.data || []).map(mapDiscount),
        creditors: (creditorsResponse.data || []).map(mapCreditor),
        orders: (ordersResponse.data || []).map(mapOrder),
        itemCancellations: (itemCancellationsResponse.data || []).map(
          mapItemCancellation,
        ),
        accommodation: accommodationSummary ?? undefined,
      };

      salesReportCache.set(cacheKey, {
        data: report,
        fetchedAt: Date.now(),
      });

      return {
        success: true,
        data: report,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch sales report',
        data: undefined,
      };
    }
  })();

  salesReportInFlight.set(cacheKey, request);

  try {
    return await request;
  } finally {
    salesReportInFlight.delete(cacheKey);
  }
};

export const fetchOutletSalesReport = async ({
  outletId,
  companyId,
  outletName,
  businessDate,
}: {
  outletId: string;
  companyId: number;
  outletName: string;
  businessDate: string;
}): Promise<ApiResponse<OutletSalesReport>> =>
  fetchScopedSalesReport({
    outletId,
    companyId,
    outletName,
    businessDate,
  });

export const exportSalesReportPdf = async (
  request: ExportSalesReportPdfRequest,
): Promise<ApiResponse<ExportSalesReportPdfResponse>> => {
  if (!isElectron()) {
    const { data, error } = await supabase.functions.invoke(
      'export-sales-report-pdf',
      {
        body: {
          outletName: request.outletName,
          businessDate: request.businessDate,
          report: request.report,
        },
      },
    );
    if (error || !data?.pdfBase64) {
      return {
        success: false,
        error: error?.message ?? 'Failed to generate PDF',
      };
    }
    downloadPdfFromBase64(data.pdfBase64, data.filename);
    return { success: true, data: { filePath: '' } };
  }

  try {
    const response = (await window.electron.ipcRenderer.invoke(
      REPORTS_IPC_CHANNELS.EXPORT_SALES_REPORT_PDF,
      request,
    )) as ApiResponse<ExportSalesReportPdfResponse>;

    if (!response?.success || !response.data) {
      return {
        success: false,
        error: response?.error || 'Failed to export sales report PDF',
        data: undefined,
      };
    }

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to export sales report PDF',
      data: undefined,
    };
  }
};

export const sendSalesReportEmail = async ({
  outletId,
  companyId,
  outletName,
  businessDate,
}: {
  outletId: string;
  companyId: number;
  outletName: string;
  businessDate: string;
}): Promise<ApiResponse<{ recipientCount: number; message: string }>> => {
  const { data, error } = await supabase.functions.invoke(
    'send-sales-report-email',
    {
      body: {
        outletId,
        companyId,
        outletName,
        businessDate,
      },
    },
  );

  if (error) {
    return {
      success: false,
      error: error.message || 'Failed to queue sales report email',
    };
  }

  return {
    success: true,
    data: {
      recipientCount: data?.recipientCount || 0,
      message: data?.message || 'Email queued successfully',
    },
  };
};
